import React, { useState, useEffect } from 'react';
import {
  Button,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow
} from '@nextui-org/react';
import { Trash, Copy } from 'lucide-react';
import { API_URL } from '../../utils/constants';
import auth from '../../Auth';
import Breadcrumbs from '../Breadcrumbs';

function EnvironmentsPage() {
  const [envs, setEnvs] = useState([]);
  const [newEnvName, setNewEnvName] = useState('');
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const alerGreentColor = 'bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative';
  const alertRedColor = 'bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative';
  const [alertColor, setAlertColor] = useState(alerGreentColor);

  const fetchEnvs = () => {
    fetch(`${API_URL}/envs`, {
      headers: {
        Authorization: 'Bearer ' + auth.syncGetIdToken(),
        'Content-Type': 'application/json'
      }
    })
      .then((response) => response.json())
      .then((data) => setEnvs(data))
      .catch((error) => console.error('Error fetching envs:', error));
  };

  // Fetch envs
  useEffect(() => {
    fetchEnvs();
  }, []);

  // Function to delete an env
  const deleteEnv = (env_id) => {
    console.log('Deleting env:', env_id);
    fetch(`${API_URL}/envs/${env_id}`, {
      method: 'DELETE',
      headers: {
        Authorization: 'Bearer ' + auth.syncGetIdToken(),
        'Content-Type': 'application/json'
      }
    })
      .then(() => {
        fetchEnvs();
      })
      .catch((error) => console.error('Error deleting env:', error));
  };

  const createNewEnv = () => {
    if (!newEnvName) {
      setAlertMessage('Please enter a name for the new environment');
      setAlertColor(alertRedColor);
      setShowAlert(true);
      setTimeout(() => setShowAlert(false), 3000);
      return;
    }
    fetch(`${API_URL}/envs`, {
      method: 'POST',
      headers: {
        Authorization: 'Bearer ' + auth.syncGetIdToken(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ env_name: newEnvName })
    })
      // check if the response is ok
      .then((response) => {
        if (response.status === 200) {
          setAlertMessage('New environment created successfully!');
          setAlertColor(alerGreentColor);
          setShowAlert(true);
          setNewEnvName('');
          fetchEnvs();
          setTimeout(() => setShowAlert(false), 3000);
        } else {
          throw new Error('Error creating environment');
        }
      })
      .catch((error) => {
        setAlertColor(alerGreentColor);
        console.error('Error creating env:', error);
        setAlertMessage('Error creating environment. Please try again.');
        setShowAlert(true);
        setTimeout(() => setShowAlert(false), 3000);
      });
  };

  function tmsToDateTime(tms) {
    tms = parseInt(tms);
    return new Date(tms).toUTCString();
  }
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const crumbs = [
    { path: '/', breadcrumb: 'Home' },
    { path: '/settings', breadcrumb: 'Settings' },
    { path: window.location.href, breadcrumb: 'Environments' }
  ];
  return (
    <div className='bg-white p-8'>
      <Breadcrumbs crumbs={crumbs} />
      <div className='flex gap-2 justify-start items-center mt-8'>
        <Input value={newEnvName} onChange={(e) => setNewEnvName(e.target.value)} className='w-48' />
        <Button color='primary' onClick={createNewEnv}>
          Create New Environment
        </Button>
      </div>
      {showAlert && (
        <div className={alertColor} role='alert'>
          <strong className='font-bold'>{alertColor === alerGreentColor ? 'Success!' : 'Error!'}</strong>
          <span className='block sm:inline'>{alertMessage}</span>
          <span className='absolute top-0 bottom-0 right-0 px-4 py-3' onClick={() => setShowAlert(false)}>
            <svg className='fill-current h-6 w-6 text-black-500' role='button' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'>
              <title>Close</title>
              <path d='M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.029a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.697z' />
            </svg>
          </span>
        </div>
      )}
      <Table isHeaderSticky className='mt-4' aria-label='Environments Table'>
        <TableHeader>
          <TableColumn style={{ width: '40%' }}>ENVIRONMENT</TableColumn>
          <TableColumn style={{ width: '40%' }}>CREATED AT</TableColumn>
          <TableColumn style={{ width: '20%' }}>ACTIONS</TableColumn>
        </TableHeader>
        <TableBody>
          {envs.map((env) => (
            <TableRow key={env.env_name}>
              <TableCell>{env.env_name}</TableCell>
              <TableCell>{tmsToDateTime(env.created_tms)}</TableCell>
              <TableCell>
                <Popover isOpen={isPopoverOpen} onOpen={() => setIsPopoverOpen(true)} onClose={() => setIsPopoverOpen(false)}>
                  <PopoverTrigger>
                    <Button color='danger' aria-label='delete token' onClick={() => setIsPopoverOpen(!isPopoverOpen)}>
                      <Trash className='h-4 w-4' />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent>
                    <div className='p-4'>
                      <p className='font-bold mb-2'>Are you sure?</p>
                      <p className='text-sm mb-4'>This action cannot be undone. This will permanently delete this Environment</p>
                      <div className='flex justify-end gap-2'>
                        <Button color='default' aria-label='cancel delete environment' onClick={() => setIsPopoverOpen(false)}>
                          Cancel
                        </Button>
                        <Button
                          color='danger'
                          aria-label='confirm delete environment'
                          onClick={() => {
                            deleteEnv(env.env_name);
                            setIsPopoverOpen(false);
                          }}
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export default EnvironmentsPage;
